import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { X } from "lucide-react";

import { authRepository } from "@/infrastructure/auth/auth.repository";
import { useMeetingDetail } from "@/application/use-cases/meetings/use-meetings.queries";

export const Route = createFileRoute("/meetings/$id/present")({
  beforeLoad: async () => {
    const session = await authRepository.getSession();
    if (!session) {
      throw redirect({ to: "/login" });
    }
  },
  component: MeetingPresentPage,
});

function MeetingPresentPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const detailQ = useMeetingDetail(id);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        navigate({ to: "/meetings/$id", params: { id } });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, id]);

  if (detailQ.isLoading) {
    return (
      <div className="bg-background text-muted-foreground flex h-screen w-screen items-center justify-center text-xs italic">
        Loading…
      </div>
    );
  }

  const html = detailQ.data?.presentation_html;
  if (!html) {
    return (
      <div className="bg-background flex h-screen w-screen flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground text-sm">No presentation set for this meeting.</p>
        <button
          type="button"
          onClick={() => navigate({ to: "/meetings/$id", params: { id } })}
          className="text-xs underline"
        >
          Back to meeting
        </button>
      </div>
    );
  }

  return (
    <div className="bg-background relative h-screen w-screen overflow-hidden">
      <iframe
        title={detailQ.data?.title ?? "Presentation"}
        srcDoc={html}
        sandbox="allow-scripts"
        className="h-full w-full border-0"
      />
      <button
        type="button"
        onClick={() => navigate({ to: "/meetings/$id", params: { id } })}
        className="bg-background/80 hover:bg-background fixed top-3 right-3 flex size-9 items-center justify-center rounded-full shadow-[0_0_0_1px_oklch(0.55_0.17_190_/_0.18),0_2px_8px_oklch(0.55_0.17_190_/_0.10)] backdrop-blur transition"
        aria-label="Close presentation"
        title="Close (Esc)"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}
